import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Menu,
  X,
} from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useGetProducts } from "@/hooks/api/query/useGetProducts";
import {
  dimLabels,
  getCreditsRangeLabel,
  getServiceCredits,
  nis2Labels,
  riskLabels,
  services as calculatorServices,
  stageLabels,
  type PricingDimension,
  type PricingNis2Class,
  type PricingRiskLevel,
  type PricingService,
  type PricingServiceType,
  type PricingStage,
} from "@/data/pricingCalculator";
import { findRecommendedProduct } from "@/utils/packageRecommendation";
import { useAuth } from "@/contexts/AuthContext";
import { useStartEvaluation } from "@/hooks/useStartEvaluation";

const formatCurrencyLabel = (currency: string): string => {
  const upper = currency.toUpperCase();
  return upper === "EUR" ? "EUR" : upper;
};

const stageAccent: Record<PricingStage, string> = {
  S1: "#3B82F6",
  S2: "#22C55E",
  S3: "#FF8400",
  S4: "#DC2626",
};

const typeBadge: Record<
  PricingServiceType,
  { bg: string; color: string; label: string }
> = {
  online: { bg: "#E0F2FE", color: "#0369A1", label: "Online" },
  download: { bg: "#FFF7ED", color: "#C2410C", label: "Descărcare" },
  ai: { bg: "#EDE9FE", color: "#6D28D9", label: "AI" },
  b2b: { bg: "#FEF3C7", color: "#92400E", label: "B2B separat" },
};

const TypeBadge = ({ type }: { type: PricingServiceType }) => {
  const m = typeBadge[type];
  return (
    <span
      className="pr-type-badge"
      style={{ background: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  );
};

const Pricing = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isLoggedIn = !!user;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollRafId = useRef(0);

  const [dim, setDim] = useState<PricingDimension>("MI");
  const [nis2, setNis2] = useState<PricingNis2Class>("Imp");
  const [risk, setRisk] = useState<PricingRiskLevel>("B");
  const [openStages, setOpenStages] = useState<Set<PricingStage>>(
    new Set(["S1"])
  );
  const toggleStage = useCallback((stage: PricingStage) => {
    setOpenStages((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) {
        next.delete(stage);
      } else {
        next.add(stage);
      }
      return next;
    });
  }, []);

  const { data: productsData, isLoading: isLoadingProducts } = useGetProducts();
  const apiPackages = productsData?.products ?? [];

  const freeServices = useMemo(
    () => calculatorServices.filter((s) => s.free),
    []
  );
  const paidServices = useMemo(
    () => calculatorServices.filter((s) => !s.free && !s.isB2B),
    []
  );

  const { totalFree, totalPaid } = useMemo(() => {
    const free = freeServices.reduce(
      (sum, s) => sum + (getServiceCredits(s, dim, nis2, risk) ?? 0),
      0
    );
    const paid = paidServices.reduce(
      (sum, s) => sum + (getServiceCredits(s, dim, nis2, risk) ?? 0),
      0
    );
    return { totalFree: free, totalPaid: paid };
  }, [dim, nis2, risk, freeServices, paidServices]);

  const recBundle = useMemo(
    () => findRecommendedProduct(totalPaid, apiPackages),
    [totalPaid, apiPackages]
  );

  const packages = apiPackages;

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (scrollRafId.current) return;
      scrollRafId.current = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 600);
        scrollRafId.current = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafId.current) cancelAnimationFrame(scrollRafId.current);
    };
  }, []);

  const startEvaluation = useStartEvaluation();

  const requestDemo = () => navigate("/contact");

  const renderServiceMeta = (s: PricingService) => (
    <div className="pr-feature-meta">
      <TypeBadge type={s.type} />
      <span className="pr-feature-credits">{getCreditsRangeLabel(s)}</span>
      {s.flagship && <span className="pr-feature-flagship">FLAGSHIP</span>}
      {s.draft && <span className="pr-feature-draft">Draft DNSC</span>}
      {s.comingSoon && <span className="pr-feature-soon">În curând</span>}
    </div>
  );

  const stageOrder: PricingStage[] = ["S1", "S2", "S3", "S4"];

  return (
    <div className="pr-page">
      <style>{`
        .pr-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .pr-page { background: #F7FAFF; color: #0F172A; font-family: 'Roboto Mono', monospace; }
        .pr-page a, .pr-page button { font-family: inherit; }
        .pr-page button:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
        .pr-wrap { width: min(1296px, calc(100% - 144px)); margin: 0 auto; }

        .pr-eyebrow { font-size: 12px; letter-spacing: 1.4px; color: #2563EB; font-weight: 500; font-family: 'Roboto Mono', monospace; }
        .pr-h2 { font-family: 'Sora', sans-serif; font-size: clamp(32px, 4vw, 42px); line-height: 1.08; letter-spacing: -1.3px; font-weight: 700; color: #0F172A; }
        .pr-sub { font-size: 14px; line-height: 1.5; color: #475569; max-width: 700px; }
        .pr-btn { border: none; border-radius: 999px; padding: 14px 28px; font-weight: 500; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 150ms ease; text-decoration: none; font-family: 'Roboto Mono', monospace; }
        .pr-btn-primary { background: #16A34A; color: #fff; }
        .pr-btn-primary:hover { background: #22C55E; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(34,197,94,0.3); }
        .pr-btn-secondary { background: #F2F3F0; color: #111111; border: 1px solid #CBCCC9; box-shadow: 0 1px 1.75px rgba(0,0,0,0.05); }
        .pr-btn-secondary:hover { background: #E7E8E5; }
        .pr-btn-ghost-light { background: transparent; color: #93C5FD; border: 1px solid #334155; }
        .pr-btn-ghost-light:hover { border-color: #60A5FA; color: #BFDBFE; }
        .pr-btn-green { background: #16A34A; color: #FFFFFF; border: 1px solid #15803D; box-shadow: 0 4px 14px rgba(22,163,74,0.2); }
        .pr-btn-green:hover { background: #15803D; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(22,163,74,0.3); }

        /* ===== NAV ===== */
        .pr-nav-bar { background: linear-gradient(90deg, #0B1220 0%, #111827 50%, #0B1220 100%); }
        .pr-nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 0; }
        .pr-nav-section { background: #0F172A; }
        .pr-nav { display: flex; align-items: center; justify-content: space-between; padding: 28px 0; max-width: 1200px; margin: 0 auto; padding-left: 24px; padding-right: 24px; width: 100%; }
        .pr-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; background: none; border: none; padding: 0; }
        .pr-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; }
        .pr-logo-text { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #FFFFFF; }
        .pr-nav-links { display: flex; align-items: center; gap: 24px; }
        .pr-nav-link { font-size: 12px; color: #CBD5E1; cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; font-family: inherit; }
        .pr-nav-link:hover { color: #FFFFFF; }
        .pr-nav-link.active { color: #60A5FA; }
        .pr-nav-btns { display: flex; gap: 12px; align-items: center; }
        .pr-nav-ghost { font-size: 12px; background: transparent; border: 1px solid #334155; color: #CBD5E1; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 0.2s ease; font-family: inherit; }
        .pr-nav-ghost:hover { border-color: #60A5FA; color: #FFFFFF; background: rgba(96,165,250,0.08); }
        .pr-nav-cta { font-size: 12px; background: #FF8400; color: #111; border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 0.2s ease; font-family: inherit; }
        .pr-nav-cta:hover { background: #FF9520; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }
        .pr-menu-btn { display: none; border: 1px solid #334155; background: transparent; color: #E2E8F0; border-radius: 10px; width: 44px; height: 44px; align-items: center; justify-content: center; cursor: pointer; }
        @keyframes pr-menuSlideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        .pr-mobile-menu { display: none; position: fixed; inset: 0; z-index: 90; background: #0F172A; padding: 24px; flex-direction: column; justify-content: center; align-items: center; gap: 0; transform-origin: top center; }
        .pr-mobile-menu-enter { animation: pr-menuSlideDown 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .pr-mobile-menu .pr-nav-link { font-size: 20px; padding: 18px 0; width: 100%; text-align: center; border-bottom: 1px solid #1E293B; color: #E2E8F0; }
        .pr-mobile-menu .pr-nav-link:last-of-type { border-bottom: none; }
        .pr-mobile-menu .pr-btn { margin-top: 24px; width: 100%; justify-content: center; font-size: 16px; padding: 18px 28px; }
        .pr-mobile-menu-close { position: absolute; top: 24px; right: 24px; }

        /* ===== HERO ===== */
        .pr-hero { background: #0F172A; padding: 80px 0; text-align: center; }
        .pr-hero-inner { display: flex; flex-direction: column; align-items: center; gap: 20px; }
        .pr-hero-eyebrow { font-size: 12px; letter-spacing: 1.4px; color: #60A5FA; font-weight: 500; }
        .pr-hero-title { font-family: 'Sora', sans-serif; font-size: clamp(36px, 5vw, 48px); line-height: 1.06; letter-spacing: -2px; font-weight: 800; color: #FFFFFF; max-width: 1000px; }
        .pr-hero-sub { font-size: 14px; line-height: 1.5; color: #C7D2FE; max-width: 900px; }
        .pr-hero-pill { display: inline-flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 999px; padding: 10px 20px; }
        .pr-hero-pill-num { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: #22C55E; }
        .pr-hero-pill-text { font-size: 13px; color: #FFFFFF; }
        .pr-hero-foot { font-size: 11px; color: #94A3B8; }

        /* ===== COMPARISON ===== */
        .pr-comparison { background: #FFFFFF; padding: 80px 0; }
        .pr-comparison-header { display: flex; flex-direction: column; gap: 12px; margin-bottom: 40px; }
        .pr-comparison-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: stretch; }
        .pr-card-free { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 32px 36px; display: flex; flex-direction: column; gap: 12px; }
        .pr-card-paid { background: #0F172A; border: 1px solid #1E293B; border-radius: 12px; padding: 32px 36px; display: flex; flex-direction: column; gap: 12px; }
        .pr-card-title { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; letter-spacing: -0.5px; }
        .pr-card-sub { font-size: 12px; line-height: 1.45; }
        .pr-card-divider-light { height: 1px; background: #E2E8F0; margin: 4px 0; }
        .pr-card-divider-dark { height: 1px; background: #1E293B; margin: 4px 0; }
        .pr-feature-item { display: flex; align-items: flex-start; gap: 8px; font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; line-height: 1.4; }
        .pr-feature-item-content { display: flex; flex-direction: column; gap: 4px; }
        .pr-feature-included { color: #0F172A; }
        .pr-feature-excluded { color: #94A3B8; }
        .pr-feature-paid { color: #FFFFFF; }
        .pr-check-green { color: #22C55E; flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; }
        .pr-check-white { color: #FFFFFF; flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; }
        .pr-x-red { color: #EF4444; flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; }
        .pr-feature-meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-family: 'Roboto Mono', monospace; font-weight: 500; }
        .pr-feature-credits { font-size: 10px; color: #94A3B8; letter-spacing: 0.3px; }
        .pr-feature-flagship { font-size: 9px; font-weight: 700; color: #FF8400; letter-spacing: 0.5px; }
        .pr-feature-draft { font-size: 9px; font-weight: 600; color: #0369A1; background: #E0F2FE; padding: 2px 6px; border-radius: 3px; }
        .pr-feature-soon { font-size: 9px; font-weight: 600; color: #6D28D9; background: #EDE9FE; padding: 2px 6px; border-radius: 3px; }
        .pr-type-badge { font-family: 'Roboto Mono', monospace; font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 4px; white-space: nowrap; letter-spacing: 0.3px; }
        .pr-card-footnote { font-family: 'Roboto Mono', monospace; font-size: 10px; line-height: 1.6; margin-top: 8px; }

        /* ===== CALCULATOR ===== */
        .pr-calc { background: #FFFFFF; padding: 80px 0; border-top: 1px solid #F1F5F9; }
        .pr-calc-header { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; }
        .pr-calc-selectors { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 24px; }
        .pr-calc-selector-label { font-family: 'Roboto Mono', monospace; font-size: 11px; font-weight: 600; color: #475569; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 1px; }
        .pr-calc-pillrow { display: flex; gap: 6px; flex-wrap: wrap; }
        .pr-calc-pill { padding: 9px 16px; border-radius: 999px; font-family: 'Roboto Mono', monospace; font-size: 12px; font-weight: 500; cursor: pointer; border: 1px solid #E2E8F0; background: #FFFFFF; color: #475569; transition: all 0.15s ease; }
        .pr-calc-pill:hover:not(.active) { border-color: #94A3B8; }
        .pr-calc-pill.active { background: #0F172A; color: #FFFFFF; border-color: #0F172A; font-weight: 600; }
        .pr-calc-profile { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 14px 18px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; margin-bottom: 24px; }
        .pr-calc-profile-label { font-family: 'Roboto Mono', monospace; font-size: 11px; color: #64748B; }
        .pr-calc-profile-value { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 700; color: #0F172A; }
        .pr-calc-profile-mult { font-family: 'Roboto Mono', monospace; font-size: 10px; color: #FF8400; background: #FFF7ED; padding: 3px 10px; border-radius: 999px; font-weight: 600; }
        .pr-calc-table { border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden; margin-bottom: 22px; }
        .pr-calc-thead { display: grid; grid-template-columns: 36px 1fr 110px 90px 90px; padding: 10px 16px; background: #0F172A; font-family: 'Roboto Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: #94A3B8; gap: 8px; }
        .pr-calc-thead > div:nth-child(3),
        .pr-calc-thead > div:nth-child(4),
        .pr-calc-thead > div:nth-child(5) { text-align: center; }
        .pr-calc-stage-row { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: #F8FAFC; border-top: 1px solid #E2E8F0; border-bottom: 1px solid #E2E8F0; }
        .pr-calc-stage-row-button { border: none; cursor: pointer; width: 100%; text-align: left; background: transparent; }
        .pr-calc-stage-row-button:hover { background: #F1F5F9; }
        .pr-calc-stage-chevron { margin-left: auto; transition: transform 0.2s ease; }
        .pr-calc-stage-chevron.open { transform: rotate(180deg); }
        .pr-calc-stage-tag { font-family: 'Roboto Mono', monospace; font-size: 10px; font-weight: 700; padding: 3px 7px; border-radius: 4px; }
        .pr-calc-stage-name { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A; }
        .pr-calc-row { display: grid; grid-template-columns: 36px 1fr 110px 90px 90px; padding: 10px 16px; border-bottom: 1px solid rgba(226,232,240,0.6); align-items: center; gap: 8px; font-size: 13px; background: #FFFFFF; }
        .pr-calc-row.flagship { background: #FFF7ED; }
        .pr-calc-row.coming-soon { background: #FAFAF8; opacity: 0.75; }
        .pr-calc-row-id { font-family: 'Roboto Mono', monospace; font-size: 10px; color: #94A3B8; }
        .pr-calc-row-name { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 13px; color: #0F172A; }
        .pr-calc-row-name.flagship-text { font-weight: 600; }
        .pr-calc-row-credits { text-align: center; font-family: 'Roboto Mono', monospace; font-size: 13px; font-weight: 600; color: #0F172A; }
        .pr-calc-row-credits.muted { color: #94A3B8; }
        .pr-calc-row-status { text-align: center; }
        .pr-calc-status-pill { font-family: 'Roboto Mono', monospace; font-size: 9px; font-weight: 600; padding: 3px 7px; border-radius: 4px; }
        .pr-calc-status-free { color: #15803D; background: #DCFCE7; }
        .pr-calc-status-paid { color: #C2410C; background: #FFF7ED; }
        .pr-calc-status-b2b { color: #92400E; background: #FEF3C7; }
        .pr-calc-status-soon { color: #6D28D9; background: #EDE9FE; }
        .pr-calc-totals-free { display: grid; grid-template-columns: 36px 1fr 110px 90px 90px; padding: 12px 16px; background: #F8FAFC; border-top: 2px solid #E2E8F0; gap: 8px; font-size: 13px; }
        .pr-calc-totals-paid { display: grid; grid-template-columns: 36px 1fr 110px 90px 90px; padding: 12px 16px; background: #0F172A; gap: 8px; font-size: 13px; color: #FFFFFF; }
        .pr-calc-totals-label { font-family: 'Sora', sans-serif; font-weight: 600; font-size: 13px; }
        .pr-calc-totals-num { text-align: center; font-family: 'Roboto Mono', monospace; font-weight: 700; }
        .pr-calc-totals-num.free { color: #16A34A; }
        .pr-calc-totals-num.paid { color: #FF8400; }
        .pr-calc-totals-cap { text-align: center; font-family: 'Roboto Mono', monospace; font-size: 10px; }
        .pr-calc-totals-cap.free { color: #16A34A; }
        .pr-calc-totals-cap.paid { color: #FF8400; }
        .pr-calc-recommend { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; padding: 18px 22px; background: #FFF7ED; border: 1px solid rgba(255,132,0,0.25); border-radius: 12px; }
        .pr-calc-recommend-info { flex: 1; min-width: 240px; }
        .pr-calc-recommend-title { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: #0F172A; margin-bottom: 4px; }
        .pr-calc-recommend-sub { font-family: 'Roboto Mono', monospace; font-size: 11px; color: #64748B; }
        .pr-calc-recommend-prices { text-align: right; }
        .pr-calc-recommend-old { font-family: 'Roboto Mono', monospace; font-size: 12px; color: #94A3B8; text-decoration: line-through; }
        .pr-calc-recommend-new { font-family: 'Sora', sans-serif; font-size: 28px; font-weight: 800; color: #FF8400; line-height: 1.1; }
        .pr-calc-recommend-launch { font-family: 'Roboto Mono', monospace; font-size: 10px; color: #16A34A; font-weight: 600; }

        /* ===== PACKAGES ===== */
        .pr-packages { background: #F8FAFC; padding: 80px 0; }
        .pr-packages-header { display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
        .pr-packages-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 16px; }
        .pr-pkg-card { flex: 0 1 calc((100% - 48px) / 4); min-width: 0; background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 12px; padding: 24px 22px; display: flex; flex-direction: column; gap: 10px; position: relative; transition: box-shadow 200ms ease; }
        .pr-pkg-card:hover { box-shadow: 0 8px 24px rgba(15,23,42,0.08); }
        .pr-pkg-card.recommended { border: 2px solid #22C55E; }
        .pr-pkg-card-skeleton { align-items: center; justify-content: center; background: #F1F5F9; border-style: dashed; }
        .pr-pkg-skeleton-spinner { color: #94A3B8; animation: pr-spin 1s linear infinite; }
        @keyframes pr-spin { to { transform: rotate(360deg); } }
        .pr-pkg-badge { position: absolute; top: 10px; left: 22px; font-size: 11px; letter-spacing: 0.1em; font-weight: 800; color: #166534; text-transform: uppercase; }
        .pr-pkg-credits { font-family: 'Sora', sans-serif; font-size: 28px; font-weight: 800; letter-spacing: -1px; color: #0F172A; }
        .pr-pkg-name { font-family: 'Roboto Mono', monospace; font-size: 11px; font-weight: 500; color: #64748B; letter-spacing: 0.3px; margin-top: -4px; }
        .pr-pkg-old-price { font-size: 14px; font-weight: 600; color: #FF8400; text-decoration: line-through; }
        .pr-pkg-price { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: #0F172A; }
        .pr-pkg-save { font-size: 11px; font-weight: 700; color: #16A34A; background: #F0FDF4; border-radius: 4px; padding: 3px 8px; display: inline-block; width: fit-content; }
        .pr-pkg-per { font-size: 11px; color: #64748B; }

        /* ===== PAYMENT ===== */
        .pr-payment { background: #FFFFFF; padding: 80px 0; }
        .pr-payment-header { display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
        .pr-payment-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .pr-pay-card { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 28px 32px; display: flex; flex-direction: column; gap: 12px; transition: box-shadow 200ms ease; }
        .pr-pay-card:hover { box-shadow: 0 8px 24px rgba(15,23,42,0.08); }
        .pr-pay-title { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #0F172A; }
        .pr-pay-desc { font-size: 13px; line-height: 1.55; color: #475569; }
        .pr-pay-features { font-size: 12px; color: #22C55E; font-weight: 500; }
        .pr-b2b-banner { background: #0F172A; border: 1px solid #1E293B; border-radius: 12px; padding: 32px 36px; display: flex; flex-direction: column; gap: 14px; margin-top: 20px; align-items: flex-start; }
        .pr-b2b-title { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; color: #FFFFFF; }
        .pr-b2b-desc { font-size: 13px; line-height: 1.55; color: #CBD5E1; max-width: 900px; }

        /* ===== FINAL CTA ===== */
        .pr-cta { background: #111827; padding: 96px 0; }
        .pr-cta-inner { display: flex; flex-direction: column; gap: 24px; align-items: center; text-align: center; }
        .pr-cta-eyebrow { font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: #60A5FA; font-weight: 600; }
        .pr-cta-title { font-family: 'Sora', sans-serif; font-size: clamp(32px, 4.2vw, 48px); font-weight: 800; letter-spacing: -2px; line-height: 1.06; color: #FFFFFF; max-width: 800px; }
        .pr-cta-desc { font-size: 15px; line-height: 1.55; color: #C7D2FE; max-width: 700px; }
        .pr-cta-btns { display: flex; flex-wrap: wrap; gap: 12px; }

        /* ===== MOBILE CTA ===== */
        .pr-mobile-cta { display: none; position: sticky; bottom: 0; z-index: 80; background: rgba(11,18,32,0.94); backdrop-filter: blur(10px); border-top: 1px solid #334155; padding: 10px 12px; }
        .pr-mobile-cta button { width: 100%; justify-content: center; }

        /* ===== BACK-TO-TOP BUTTON ===== */
        .pr-back-to-top { position: fixed; bottom: 96px; right: 28px; z-index: 90; width: 44px; height: 44px; border-radius: 50%; background: #2563EB; color: #FFF; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 16px rgba(37,99,235,0.35); opacity: 0; transform: translateY(16px) scale(0.85); transition: opacity 0.3s ease, transform 0.3s ease, background 0.15s ease; pointer-events: none; }
        .pr-back-to-top.visible { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
        .pr-back-to-top:hover { background: #1D4ED8; transform: translateY(-2px) scale(1.05); }
        .pr-back-to-top:focus-visible { outline: 2px solid #60A5FA; outline-offset: 3px; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1180px) {
          .pr-pkg-card { flex-basis: calc((100% - 32px) / 3); }
        }
        @media (max-width: 1080px) {
          .pr-comparison-cols, .pr-payment-grid { grid-template-columns: 1fr; }
          .pr-calc-selectors { grid-template-columns: 1fr; }
          .pr-calc-thead, .pr-calc-row, .pr-calc-totals-free, .pr-calc-totals-paid { grid-template-columns: 30px 1fr 90px 70px 70px; padding-left: 12px; padding-right: 12px; }
        }
        @media (max-width: 920px) {
          .pr-wrap { width: min(1296px, calc(100% - 32px)); }
          .pr-nav-links, .pr-nav-btns { display: none; }
          .pr-menu-btn { display: inline-flex; }
          .pr-mobile-menu { display: flex; }
          .pr-mobile-cta { display: flex; }
          .pr-pkg-card { flex-basis: calc((100% - 16px) / 2); }
        }
        @media (max-width: 640px) {
          .pr-pkg-card { flex-basis: 100%; }
          .pr-calc-thead > div:nth-child(3),
          .pr-calc-row > div:nth-child(3) { display: none; }
          .pr-calc-thead, .pr-calc-row, .pr-calc-totals-free, .pr-calc-totals-paid { grid-template-columns: 24px 1fr 70px 70px; }
        }
      `}</style>

      {/* ====== NAV (V40 style) ====== */}
      <div className="pr-nav-section">
        <nav className="pr-nav">
          <button
            type="button"
            className="pr-logo"
            onClick={() => navigate("/")}
            aria-label="NIS2PASS home"
          >
            <span className="pr-logo-mark" />
            <span className="pr-logo-text">NIS2PASS</span>
          </button>
          <div className="pr-nav-links" role="navigation" aria-label="Navigare">
            <button
              type="button"
              className="pr-nav-link"
              onClick={() => navigate("/")}
            >
              Cum functioneaza
            </button>
            <button
              type="button"
              className="pr-nav-link"
              onClick={() => navigate("/")}
            >
              Module
            </button>
            <button type="button" className="pr-nav-link active">
              Preturi
            </button>
            <button type="button" className="pr-nav-link" onClick={requestDemo}>
              Contact
            </button>
          </div>
          <div className="pr-nav-btns">
            {!loading &&
              (isLoggedIn ? (
                <>
                  <button
                    type="button"
                    className="pr-nav-cta"
                    onClick={() => navigate("/companiile-mele")}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className="pr-nav-ghost"
                    onClick={() => navigate("/logout")}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="pr-nav-ghost"
                    onClick={() => navigate("/auth?mode=signin")}
                  >
                    Intra in cont
                  </button>
                  <button
                    type="button"
                    className="pr-nav-cta"
                    onClick={() => navigate("/auth")}
                  >
                    Creeaza cont gratuit
                  </button>
                </>
              ))}
          </div>
          <button
            type="button"
            className="pr-menu-btn"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X size={18} aria-hidden="true" />
            ) : (
              <Menu size={18} aria-hidden="true" />
            )}
          </button>
        </nav>
      </div>

      {mobileMenuOpen && (
        <div
          className="pr-mobile-menu pr-mobile-menu-enter"
          role="navigation"
          aria-label="Meniu mobil"
        >
          <button
            type="button"
            className="pr-menu-btn pr-mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Inchide meniu"
          >
            <X size={20} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="pr-nav-link"
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/");
            }}
          >
            Cum functioneaza
          </button>
          <button
            type="button"
            className="pr-nav-link"
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/");
            }}
          >
            Module
          </button>
          <button
            type="button"
            className="pr-nav-link active"
            onClick={() => setMobileMenuOpen(false)}
          >
            Preturi
          </button>
          <button
            type="button"
            className="pr-nav-link"
            onClick={() => {
              setMobileMenuOpen(false);
              requestDemo();
            }}
          >
            Contact
          </button>
          {isLoggedIn ? (
            <>
              <button
                type="button"
                className="pr-btn pr-btn-primary"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/companiile-mele");
                }}
              >
                Dashboard
              </button>
              <button
                type="button"
                className="pr-nav-ghost"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/logout");
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              type="button"
              className="pr-btn pr-btn-primary"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/auth");
              }}
            >
              Creeaza cont gratuit <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* ====== HERO ====== */}
      <section className="pr-hero">
        <div className="pr-wrap pr-hero-inner">
          <p className="pr-hero-eyebrow">PREȚURI CONFORMITATE NIS2</p>
          <h1 className="pr-hero-title">
            Cât costă conformitatea NIS2 — Gratuit vs credite
          </h1>
          <p className="pr-hero-sub">
            NIS2PASS este o platformă self-service — folosită de consultanți ca
            instrument de lucru sau direct de companiile care au expertiza
            necesară. Creditele sunt necesare doar pentru generarea documentelor
            oficiale și serviciile premium.
          </p>
          <div className="pr-hero-pill">
            <span className="pr-hero-pill-num">100</span>
            <span className="pr-hero-pill-text">
              credite gratuite la înregistrare
            </span>
          </div>
          <p className="pr-hero-foot">
            Prețuri per entitate (companie) evaluată · Prețuri fără TVA
          </p>
        </div>
      </section>

      {/* ====== FREE vs PAID COMPARISON (2nd fold — copy from pricing-v5) ====== */}
      <section className="pr-comparison">
        <div className="pr-wrap">
          <div className="pr-comparison-header">
            <p className="pr-eyebrow">COMPARAȚIE PLANURI</p>
            <h2 className="pr-h2">
              Ce primești gratuit și ce deblochezi cu credite
            </h2>
            <p className="pr-sub">
              Evaluarea completă NIS2 este gratuită. Creditele sunt necesare
              doar pentru documente oficiale și servicii premium.
            </p>
          </div>
          <div className="pr-comparison-cols">
            {/* Free card */}
            <div className="pr-card-free">
              <h3 className="pr-card-title" style={{ color: "#0F172A" }}>
                Cont gratuit NIS2 · 100 credite incluse
              </h3>
              <p className="pr-card-sub" style={{ color: "#64748B" }}>
                Evaluare completă fără costuri — parcurge toate fluxurile NIS2
              </p>
              <div className="pr-card-divider-light" />
              {freeServices.map((s) => (
                <div key={s.id} className="pr-feature-item pr-feature-included">
                  <Check size={16} className="pr-check-green" />
                  <span>{s.name}</span>
                </div>
              ))}
              <div className="pr-card-divider-light" />
              {paidServices.map((s) => (
                <div key={s.id} className="pr-feature-item pr-feature-excluded">
                  <X size={16} className="pr-x-red" />
                  <span>{s.name}</span>
                </div>
              ))}
              <div style={{ marginTop: "auto", paddingTop: "14px" }}>
                <button
                  type="button"
                  className="pr-btn pr-btn-secondary"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={startEvaluation}
                >
                  Începe gratuit <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Paid card */}
            <div className="pr-card-paid">
              <h3 className="pr-card-title" style={{ color: "#FFFFFF" }}>
                Credite pentru conformitate completă NIS2
              </h3>
              <p className="pr-card-sub" style={{ color: "#94A3B8" }}>
                Descarcă, transmite la DNSC, remediază conform OUG 155/2024
              </p>
              <div className="pr-card-divider-dark" />
              <div className="pr-feature-item pr-feature-paid">
                <Check size={16} className="pr-check-green" />
                <span>Tot ce include contul gratuit</span>
              </div>
              {paidServices.map((s) => (
                <div key={s.id} className="pr-feature-item pr-feature-paid">
                  <Check size={16} className="pr-check-green" />
                  <div className="pr-feature-item-content">
                    <span>{s.name}</span>
                    {renderServiceMeta(s)}
                  </div>
                </div>
              ))}
              <div className="pr-card-divider-dark" />
              <p className="pr-card-footnote" style={{ color: "#94A3B8" }}>
                S1 + S2: preț fix · S3 + S4: diferențiat pe Dimensiune ×
                Încadrare NIS2 × Nivel de Risc
                <br />
                Serviciile de consultanță se facturează separat, pe bază de
                contract B2B
              </p>
              <div style={{ marginTop: "auto", paddingTop: "14px" }}>
                <button
                  type="button"
                  className="pr-btn pr-btn-primary"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={startEvaluation}
                >
                  Cumpără credite <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== CALCULATOR ====== */}
      <section className="pr-calc">
        <div className="pr-wrap">
          <div className="pr-calc-header">
            <p className="pr-eyebrow">CALCULATOR CREDITE</p>
            <h2 className="pr-h2">
              Calculează costul exact pentru compania ta
            </h2>
            <p className="pr-sub">
              Selectează profilul companiei — prețurile se recalculează automat
            </p>
          </div>

          <div className="pr-calc-selectors">
            <div>
              <div className="pr-calc-selector-label">Dimensiune companie</div>
              <div className="pr-calc-pillrow">
                {(Object.keys(dimLabels) as PricingDimension[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`pr-calc-pill${dim === k ? " active" : ""}`}
                    onClick={() => setDim(k)}
                  >
                    {dimLabels[k]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="pr-calc-selector-label">Încadrare NIS2</div>
              <div className="pr-calc-pillrow">
                {(Object.keys(nis2Labels) as PricingNis2Class[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`pr-calc-pill${nis2 === k ? " active" : ""}`}
                    onClick={() => setNis2(k)}
                  >
                    {nis2Labels[k]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="pr-calc-selector-label">Nivel de risc CyFun</div>
              <div className="pr-calc-pillrow">
                {(Object.keys(riskLabels) as PricingRiskLevel[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`pr-calc-pill${risk === k ? " active" : ""}`}
                    onClick={() => setRisk(k)}
                  >
                    {riskLabels[k]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pr-calc-profile">
            <span className="pr-calc-profile-label">Profil selectat:</span>
            <span className="pr-calc-profile-value">
              {dimLabels[dim]} · {nis2Labels[nis2]} · {riskLabels[risk]}
            </span>
          </div>

          <div className="pr-calc-table">
            <div className="pr-calc-thead">
              <div>#</div>
              <div>Serviciu</div>
              <div>Tip</div>
              <div>Credite</div>
              <div>Status</div>
            </div>
            {stageOrder.map((stage) => (
              <Fragment key={stage}>
                <button
                  type="button"
                  className="pr-calc-stage-row pr-calc-stage-row-button"
                  onClick={() => toggleStage(stage)}
                >
                  <span
                    className="pr-calc-stage-tag"
                    style={{
                      background: `${stageAccent[stage]}1A`,
                      color: stageAccent[stage],
                    }}
                  >
                    {stage}
                  </span>
                  <span className="pr-calc-stage-name">
                    {stageLabels[stage]}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`pr-calc-stage-chevron${openStages.has(stage) ? " open" : ""}`}
                    style={{ color: "#64748B" }}
                  />
                </button>
                {openStages.has(stage) &&
                  calculatorServices
                    .filter((s) => s.stage === stage)
                    .map((s) => {
                      const credits = getServiceCredits(s, dim, nis2, risk);
                      const rowClass = s.comingSoon
                        ? "coming-soon"
                        : s.flagship
                          ? "flagship"
                          : "";
                      return (
                        <div key={s.id} className={`pr-calc-row ${rowClass}`}>
                          <div className="pr-calc-row-id">{s.id}</div>
                          <div
                            className={`pr-calc-row-name${s.flagship ? " flagship-text" : ""}`}
                          >
                            <span>{s.name}</span>
                            {s.flagship && (
                              <span className="pr-feature-flagship">
                                FLAGSHIP
                              </span>
                            )}
                            {s.draft && (
                              <span className="pr-feature-draft">
                                Draft DNSC
                              </span>
                            )}
                            {s.comingSoon && (
                              <span className="pr-feature-soon">În curând</span>
                            )}
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <TypeBadge type={s.type} />
                          </div>
                          <div
                            className={`pr-calc-row-credits${s.isB2B || s.comingSoon || credits === null ? " muted" : ""}`}
                          >
                            {s.isB2B
                              ? "—"
                              : credits === null
                                ? "Doar Esențiale"
                                : credits}
                          </div>
                          <div className="pr-calc-row-status">
                            {s.isB2B ? (
                              <span className="pr-calc-status-pill pr-calc-status-b2b">
                                B2B
                              </span>
                            ) : s.comingSoon ? (
                              <span className="pr-calc-status-pill pr-calc-status-soon">
                                În curând
                              </span>
                            ) : s.free ? (
                              <span className="pr-calc-status-pill pr-calc-status-free">
                                Gratuit
                              </span>
                            ) : (
                              <span className="pr-calc-status-pill pr-calc-status-paid">
                                Plătit
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
              </Fragment>
            ))}
            <div className="pr-calc-totals-free">
              <div />
              <div className="pr-calc-totals-label">
                Servicii gratuite (incluse în 100 credite)
              </div>
              <div />
              <div className="pr-calc-totals-num free">{totalFree}</div>
              <div className="pr-calc-totals-cap free">din 100 free</div>
            </div>
            <div className="pr-calc-totals-paid">
              <div />
              <div className="pr-calc-totals-label">
                Servicii plătite (de cumpărat)
              </div>
              <div />
              <div className="pr-calc-totals-num paid">{totalPaid}</div>
              <div className="pr-calc-totals-cap paid">credite</div>
            </div>
          </div>

          {recBundle && (
            <div className="pr-calc-recommend">
              <div className="pr-calc-recommend-info">
                <div className="pr-calc-recommend-title">
                  Pachet recomandat: {recBundle.name} ({recBundle.credits}{" "}
                  credite)
                </div>
                <div className="pr-calc-recommend-sub">
                  {totalPaid > recBundle.credits
                    ? `Necesită ${totalPaid} credite — recomandăm pachete cumulate sau ofertă B2B`
                    : `Necesită ${totalPaid} credite · ${recBundle.credits - totalPaid} credite rămase`}
                </div>
              </div>
              <div className="pr-calc-recommend-prices">
                {recBundle.originalAmount != null && (
                  <div className="pr-calc-recommend-old">
                    {(recBundle.originalAmount / 100).toFixed(2)}{" "}
                    {formatCurrencyLabel(recBundle.currency)}
                  </div>
                )}
                <div className="pr-calc-recommend-new">
                  {(recBundle.unitAmount / 100).toFixed(2)}{" "}
                  {formatCurrencyLabel(recBundle.currency)}
                </div>
                <div className="pr-calc-recommend-launch">preț de lansare</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ====== CREDIT PACKAGES ====== */}
      <section className="pr-packages">
        <div className="pr-wrap">
          <div className="pr-packages-header">
            <p className="pr-eyebrow">PACHETE CREDITE NIS2</p>
            <h2 className="pr-h2">
              Alege pachetul de credite pentru conformitatea ta NIS2
            </h2>
          </div>
          <div className="pr-packages-grid">
            {(isLoadingProducts || packages.length === 0) &&
              Array.from({ length: 4 }).map((_, idx) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton placeholders
                  key={`pricing-skeleton-${idx}`}
                  className="pr-pkg-card pr-pkg-card-skeleton"
                  style={{ minHeight: 260 }}
                  aria-busy="true"
                  aria-label="Se încarcă pachetele"
                >
                  <Loader2
                    className="pr-pkg-skeleton-spinner"
                    size={24}
                    aria-hidden
                  />
                </div>
              ))}
            {!isLoadingProducts &&
              packages.map((pkg) => {
                const price = pkg.unitAmount / 100;
                const originalPrice =
                  pkg.originalAmount != null ? pkg.originalAmount / 100 : null;
                const savings =
                  originalPrice != null ? originalPrice - price : 0;
                const priceLabel = price.toFixed(2);
                const originalPriceLabel =
                  originalPrice != null ? originalPrice.toFixed(2) : null;
                const savingsLabel = savings.toFixed(2);
                const perCredit = (price / pkg.credits).toFixed(2);
                const currency = formatCurrencyLabel(pkg.currency);
                const isRecommended = recBundle?.id === pkg.id;
                return (
                  <div
                    key={pkg.id}
                    className={`pr-pkg-card${isRecommended ? " recommended" : ""}`}
                  >
                    {isRecommended && (
                      <span className="pr-pkg-badge">RECOMANDAT</span>
                    )}
                    <span className="pr-pkg-credits">
                      {pkg.credits} credite
                    </span>
                    <span className="pr-pkg-name">{pkg.name}</span>
                    {originalPriceLabel != null && (
                      <span className="pr-pkg-old-price">
                        <s>
                          {originalPriceLabel} {currency}
                        </s>
                      </span>
                    )}
                    <span className="pr-pkg-price">
                      {priceLabel} {currency}
                    </span>
                    {originalPrice != null && savings > 0 && (
                      <span className="pr-pkg-save">
                        Economisești {savingsLabel} {currency}
                      </span>
                    )}
                    <span className="pr-pkg-per">
                      {perCredit} {currency}/credit
                    </span>
                    <button
                      type="button"
                      className={`pr-btn ${isRecommended ? "pr-btn-green" : "pr-btn-secondary"}`}
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        marginTop: "auto",
                      }}
                      onClick={startEvaluation}
                    >
                      Alege {pkg.credits} credite
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* ====== PAYMENT OPTIONS ====== */}
      <section className="pr-payment">
        <div className="pr-wrap">
          <div className="pr-payment-header">
            <p className="pr-eyebrow">OPȚIUNI DE PLATĂ CONFORMITATE NIS2</p>
            <h2 className="pr-h2">
              Alege metoda de plată potrivită organizației tale
            </h2>
          </div>
          <div className="pr-payment-grid">
            <div className="pr-pay-card">
              <h3 className="pr-pay-title">
                Instant — Plată online pentru credite NIS2
              </h3>
              <p className="pr-pay-desc">
                Prin Stripe — card bancar sau alte metode acceptate. Procesare
                rapidă cu activare imediată a creditelor pentru generarea
                documentelor NIS2.
              </p>
              <p className="pr-pay-features">
                ✓ Activare instantanee · ✓ Factură fiscală automată · ✓ Visa,
                Mastercard, Apple Pay
              </p>
            </div>
            <div className="pr-pay-card">
              <h3 className="pr-pay-title">
                B2B — Contract conformitate NIS2 + transfer bancar
              </h3>
              <p className="pr-pay-desc">
                Contract de prestări servicii cu Compliance Made Easy S.R.L.
                Ideal pentru organizații care au nevoie de facturare B2B pentru
                bugetul de conformitate NIS2.
              </p>
              <p className="pr-pay-features">
                ✓ Contract cadru · ✓ Factură proformă + fiscală · ✓ Transfer
                bancar (OP)
              </p>
            </div>
          </div>
          <div className="pr-b2b-banner">
            <h3 className="pr-b2b-title">
              Pachete B2B pentru conformitate NIS2 la scară largă
            </h3>
            <p className="pr-b2b-desc">
              Pentru organizații cu mai multe entități NIS2 sau consultanți care
              gestionează portofolii de clienți conform Directivei 2022/2555 și
              OUG 155/2024 — pachete negociate cu discount-uri de volum
              suplimentare.
            </p>
            <button
              type="button"
              className="pr-btn pr-btn-primary"
              onClick={requestDemo}
            >
              Contactează-ne pentru ofertă B2B <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="pr-cta">
        <div className="pr-wrap pr-cta-inner">
          <p className="pr-cta-eyebrow">ÎNCEPE ACUM</p>
          <h2 className="pr-cta-title">
            Pregătit să parcurgi conformitatea NIS2?
          </h2>
          <p className="pr-cta-desc">
            Creează un cont gratuit și parcurge întreg fluxul de evaluare NIS2
            pentru compania ta. 100 de credite incluse — suficiente pentru
            identificare, evaluare risc ENIRE@RO și autoevaluare maturitate
            CyFun®.
          </p>
          <div className="pr-cta-btns">
            <button
              type="button"
              className="pr-btn pr-btn-primary"
              onClick={startEvaluation}
            >
              Începe evaluarea — 100 credite gratuite <ArrowRight size={16} />
            </button>
            <button
              type="button"
              className="pr-btn pr-btn-ghost-light"
              onClick={requestDemo}
            >
              Solicită demo
            </button>
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <SharedFooter onPreturiClick={scrollToTop} />

      {/* ====== BACK TO TOP ====== */}
      <button
        type="button"
        className={`pr-back-to-top${showBackToTop ? " visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Inapoi sus"
      >
        <ChevronUp size={20} aria-hidden="true" />
      </button>

      {/* ====== MOBILE STICKY CTA ====== */}
      <div className="pr-mobile-cta">
        <button
          type="button"
          className="pr-btn pr-btn-primary"
          onClick={startEvaluation}
        >
          Începe evaluarea — 100 credite gratuite <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pricing;

// Re-exported for tests / external consumers that want the unmemoized base data
export { calculatorServices };
